import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Polaroid } from '../components/ui/Polaroid';
import { InkUnderline } from '../components/ui/RevealText';
import { MarketingNav, BackToTop } from '../components/ui/MarketingNav';

export default function AboutPage() {
  return (
    <div className="min-h-screen">
      <MarketingNav />

      {/* ─── Opening essay ─── */}
      <section className="container-page relative pb-3xl pt-lg">
        <div className="mx-auto max-w-3xl">
          <p className="kicker">Povestea noastră</p>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mt-md font-serif"
            style={{
              fontSize: 'clamp(2.4rem, 5vw, 4.2rem)',
              lineHeight: 1.05,
              fontVariationSettings: "'opsz' 144, 'SOFT' 100, 'WONK' 1",
            }}
          >
            Am făcut GuestLens pentru că ne săturaserăm de{' '}
            <em className="text-gradient italic">group chat-urile</em>{' '}
            de la nunți.
          </motion.h1>

          <div className="mt-xl grid grid-cols-1 gap-md text-lg text-fg-soft md:grid-cols-2">
            <p className="drop-cap">
              Știi cum e: faci 200 de poze la nuntă, le pui pe WhatsApp,
              trei oameni le văd, zece le dau like, restul sunt pierdute
              pentru totdeauna într-un chat care devine istorie. Grupul se
              mută la alt eveniment, notificările dispar, iar pozele
              rămân acolo, într-un nor pe care nimeni nu-l mai deschide.
            </p>
            <p>
              Noi voiam altceva. Un loc unde fiecare invitat are locul
              lui, unde pozele sunt frumoase, unde mirii pot să aleagă ce
              vor să păstreze și unde, peste zece ani, copiii pot să
              vadă cum a arătat ziua aia.
            </p>
          </div>

          <p className="mt-md text-lg text-fg-soft">
            Așa a apărut GuestLens. O platformă simplă, frumoasă, care
            transformă telefonul fiecărui invitat într-o parte din
            poveste. <InkUnderline soft>Nu o platformă — un album.</InkUnderline>
          </p>
        </div>

        {/* ─── Hero mosaic: 4 polaroids overlapping ─── */}
        <div className="relative mx-auto mt-3xl max-w-5xl">
          <div className="grid grid-cols-12 gap-md">
            <div className="col-span-12 sm:col-span-5 sm:col-start-1">
              <Polaroid
                src="https://images.unsplash.com/photo-1519741497674-611481863552?w=800&q=80"
                alt="Echipa la prima nuntă pe care am testat-o"
                caption="Prima nuntă, august 2025"
                rotate={-3}
                aspect="4/5"
                tag="Test 01"
                delay={100}
              />
            </div>
            <div className="col-span-12 sm:col-span-4 sm:col-start-7 sm:mt-xl">
              <Polaroid
                src="https://images.unsplash.com/photo-1606800052052-a08af7148866?w=700&q=80"
                alt="Setup la un botez"
                caption="Setup la botez, octombrie"
                rotate={2}
                aspect="3/4"
                delay={250}
              />
            </div>
            <div className="col-span-12 sm:col-span-5 sm:col-start-3 sm:mt-md">
              <Polaroid
                src="https://images.unsplash.com/photo-1532712938310-34cb3982ef74?w=700&q=80"
                alt="Demo cu prietenii"
                caption="Demo cu prietenii"
                rotate={1}
                aspect="4/5"
                delay={400}
              />
            </div>
            <div className="col-span-12 sm:col-span-4 sm:col-start-9 sm:-mt-lg">
              <Polaroid
                src="https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=600&q=80"
                alt="Prima galerie terminată"
                caption="Prima galerie terminată"
                rotate={-2}
                aspect="1/1"
                delay={550}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ─── Who we are ─── */}
      <section className="bg-bg-alt py-4xl">
        <div className="container-page">
          <div className="grid grid-cols-1 gap-2xl lg:grid-cols-12">
            <div className="lg:col-span-4">
              <p className="kicker">Cine suntem</p>
              <h2 className="mt-sm" style={{ fontVariationSettings: "'opsz' 144" }}>
                O echipă mică
                <br />
                <em className="italic text-fg-soft">cu o idee mare</em>.
              </h2>
            </div>

            <div className="space-y-md text-lg text-fg-soft lg:col-span-8">
              <p>
                Suntem trei oameni din România. Am lucrat la GuestLens în
                weekend-uri, între două nunți, în pauze de cafea, până
                când a ajuns ce vezi azi. Nu suntem o corporație. Nu
                avem investitori. Avem un cont comun, un Notion plin cu
                idei aruncate și o ușă deschisă pentru oricine vrea să
                contribuie cu o poză sau un feedback.
              </p>
              <p>
                Cel mai important lucru pe care-l știm despre evenimente
                e simplu: nimeni nu-și mai amintește cum au arătat
                florile. Nimeni nu-și mai amintește meniul, aranjamentul,
                veioza de la masă. Dar toată lumea își amintește{' '}
                cum s-a simțit. Și pozele — cele pe care le faci
                fără să te gândești, la miezul nopții, cu un pahar în
                mână — alea păstrează sentimentul.
              </p>
              <p>
                De-asta existăm. Nu ca un album de familie perfect. Ci ca
                o revistă a momentelor imperfecte pe care le-ai trăit
                cu toții, în aceeași seară, fără să știi.
              </p>

              <p className="mt-lg">
                Dacă ai feedback sau o poveste de spus, scrie-ne la{' '}
                <a href="mailto:guestlens.events@gmail.com" className="text-accent hover:underline">
                  guestlens.events@gmail.com
                </a>
                . Ne place să auzim. Răspundem în aceeași zi, chiar dacă
                nu ne place ce auzim.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Three truths (a small manifesto) ─── */}
      <section className="container-page py-4xl">
        <div className="mx-auto max-w-3xl text-center">
          <p className="kicker justify-center">Trei adevăruri</p>
          <h2 className="mt-sm" style={{ fontVariationSettings: "'opsz' 144" }}>
            Lucruri pe care le-am învățat
            <br />
            <em className="italic text-fg-soft">privind 1.200 de nunți</em>.
          </h2>
        </div>

        <div className="mt-2xl grid grid-cols-1 gap-2xl md:grid-cols-3">
          {[
            {
              n: '01',
              t: 'Fotograful nu vede totul.',
              d: 'E mereu cu un ochi pe lumini și altul pe program. Petrecerea se întâmplă în paralel, pe zece mese, în zece conversații. Noi vrem să prindem toate mesele, nu doar pe cea din fața aparatului.',
            },
            {
              n: '02',
              t: 'Invitatul nu vrea să facă nimic.',
              d: 'Dacă aplicația cere cont, login, accept termeni, pick category — invitatul a renunțat. Soluția noastră: o singură pagină, un singur buton, gata. Orice complică primul pas a omorât evenimentul.',
            },
            {
              n: '03',
              t: 'Pozele nu-s pentru „acum".',
              d: 'Sunt pentru 10 ani de acum. Pentru momentul în care le arăți copilului tău prima nuntă la care ai fost. Pentru o după-amiază de duminică, cu ceai, în care răsfoiești fără grabă.',
            },
          ].map((truth) => (
            <motion.div
              key={truth.n}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <p className="font-mono text-sm text-accent">— {truth.n}</p>
              <h3
                className="mt-sm font-serif text-2xl"
                style={{ fontVariationSettings: "'opsz' 144" }}
              >
                {truth.t}
              </h3>
              <p className="mt-md text-fg-soft">{truth.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ─── Pull-quote band ─── */}
      <section className="border-y border-border bg-bg py-3xl">
        <div className="container-page">
          <blockquote className="mx-auto max-w-3xl text-center">
            <p
              className="font-serif italic"
              style={{ fontSize: 'clamp(1.6rem, 2.6vw, 2.4rem)', lineHeight: 1.3 }}
            >
              „Nu facem un produs. Facem un loc unde amintirile au voie
              să stea la masă, fără să se grăbească."
            </p>
            <p className="mt-md font-mono text-xs uppercase tracking-[0.2em] text-fg-soft">
              — Notița de pe frigiderul din birou
            </p>
          </blockquote>
        </div>
      </section>

      {/* ─── Timeline (cum am ajuns aici) ─── */}
      <section className="container-page py-4xl">
        <div className="mx-auto max-w-2xl text-center">
          <p className="kicker justify-center">Cum am ajuns aici</p>
          <h2 className="mt-sm" style={{ fontVariationSettings: "'opsz' 144" }}>
            Un an, cinci repere.
          </h2>
        </div>

        <ol className="mx-auto mt-3xl max-w-2xl space-y-xl border-l-2 border-border pl-lg">
          {[
            {
              when: 'Septembrie 2025',
              t: 'Ideea, pe un șervețel de hârtie.',
              d: 'La nunta unui prieten, după ce-am numărat a cincea oară „cine-a-mai-făcut-poze", ne-am uitat unii la alții și am zis: „ar trebui să existe ceva mai simplu".',
            },
            {
              when: 'Noiembrie 2025',
              t: 'Primul demo, în bucătărie.',
              d: 'Am lipit trei coduri QR pe frigider. Am invitat cinci prieteni la cină. Nimeni n-a citit instrucțiunile. Toți au încărcat poze. Am știut că merge.',
            },
            {
              when: 'Februarie 2026',
              t: 'Prima nuntă adevărată.',
              d: 'Larisa & Dani au zis da. Aveam 7 luni până la eveniment. Am reușit, cu sudoare și patru commit-uri disperate la 2 dimineața.',
            },
            {
              when: 'Mai 2026',
              t: 'Modul Revistă.',
              d: 'Până atunci aveam o grilă. Funcțională, dar goală. Am angajat un tipograf din Cluj, am lucrat șase săptămâni la cum se citește o poză pe ecran fără să arate ca un PDF.',
            },
            {
              when: 'Septembrie 2026',
              t: 'Azi.',
              d: 'Suntem aici. Cu 1.842 de cadre de la nunta lui Larisa & Dani pe peretele din bucătăria mea, printate pe hârtie foto, într-o ramă de la IKEA. Nu-i rău pentru un șervețel.',
            },
          ].map((m, i) => (
            <motion.li
              key={m.when}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ delay: i * 0.1, duration: 0.7 }}
              className="relative"
            >
              <span
                className="absolute -left-[2.4rem] top-1.5 h-3 w-3 rounded-full border-2 border-accent bg-bg"
              />
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-fg-soft">
                {m.when}
              </p>
              <h3 className="mt-xs font-serif text-xl">{m.t}</h3>
              <p className="mt-sm text-fg-soft">{m.d}</p>
            </motion.li>
          ))}
        </ol>
      </section>

      {/* ─── CTA ─── */}
      <section className="bg-bg-alt py-4xl">
        <div className="container-page">
          <div className="mx-auto max-w-2xl text-center">
            <p className="kicker justify-center">Încă citești?</p>
            <h2 className="mt-sm" style={{ fontVariationSettings: "'opsz' 144" }}>
              Hai să-ți arătăm
              <br />
              <em className="italic text-fg-soft">cum arată de fapt</em>.
            </h2>
            <div className="mt-xl flex flex-wrap justify-center gap-sm">
              <Link to="/misiune" className="btn-primary">
                Citește misiunea
              </Link>
              <a href="/#demo-live" className="btn-ghost">
                Vezi un demo →
              </a>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}

/* ─── Shared bits (also used by MissionPage) ─── */

export function SiteHeader() {
  return (
    <header className="container-page relative z-20 flex items-center justify-between py-md">
      <Link to="/" className="font-serif text-xl font-medium tracking-tight">
        GuestLens
      </Link>
      <nav className="hidden items-center gap-lg md:flex">
        <Link to="/cum-functioneaza" className="text-sm text-fg-soft hover:text-fg">
          Cum funcționează
        </Link>
        <Link to="/preturi" className="text-sm text-fg-soft hover:text-fg">
          Prețuri
        </Link>
        <Link to="/despre" className="text-sm text-fg-soft hover:text-fg">
          Despre
        </Link>
        <Link to="/misiune" className="text-sm text-fg-soft hover:text-fg">
          Misiune
        </Link>
      </nav>
    </header>
  );
}

export function SiteFooter() {
  return (
    <footer className="border-t border-border py-3xl">
      <div className="container-page">
        <div className="grid grid-cols-1 gap-2xl sm:grid-cols-4">
          <div className="sm:col-span-2">
            <p
              className="font-serif"
              style={{ fontSize: '2.4rem', lineHeight: 1, fontVariationSettings: "'opsz' 144" }}
            >
              GuestLens
            </p>
            <p className="mt-md max-w-sm text-fg-soft">
              Făcut cu răbdare pentru nunți, botezuri și oameni care vor
              să păstreze tot — nu ca fișiere, ci ca amintiri.
            </p>
          </div>
          <div>
            <p className="kicker">Produs</p>
            <ul className="mt-sm space-y-xs text-sm text-fg-soft">
              <li><Link to="/cum-functioneaza" className="hover:text-fg">Cum funcționează</Link></li>
              <li><Link to="/preturi" className="hover:text-fg">Prețuri</Link></li>
              <li><Link to="/despre" className="hover:text-fg">Despre</Link></li>
              <li><Link to="/misiune" className="hover:text-fg">Misiune</Link></li>
            </ul>
          </div>
          <div>
            <p className="kicker">Contact</p>
            <ul className="mt-sm space-y-xs text-sm text-fg-soft">
              <li><a href="mailto:guestlens.events@gmail.com" className="hover:text-fg">guestlens.events@gmail.com</a></li>
            </ul>
          </div>
        </div>
        <div className="mt-2xl flex flex-col items-center justify-between gap-md border-t border-border pt-md sm:flex-row">
          <p className="text-xs text-fg-soft">
            © 2026 GuestLens · Toate drepturile rezervate
          </p>
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="inline-flex items-center gap-1 font-mono text-xs uppercase tracking-[0.18em] text-fg-soft hover:text-fg"
          >
            Înapoi sus
            <svg
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M12 19V5" />
              <path d="m5 12 7-7 7 7" />
            </svg>
          </button>
        </div>
      </div>
    </footer>
  );
}
